import { forwardRef } from 'react';
import type { AnchorHTMLAttributes, ReactNode } from 'react';

import { cx } from '../utils/cx.js';
import type { ButtonSize, ButtonVariant } from './Button.js';
import styles from './Button.module.css';

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leading?: ReactNode;
  trailing?: ReactNode;
}

/**
 * A link that looks like a button.
 *
 * A separate component rather than a `as="a"` prop on Button, because the two
 * are genuinely different elements: an anchor takes href and target, a button
 * takes type and disabled, and a disabled anchor is not a thing. Collapsing
 * them into one component means a prop set where half the options are invalid
 * at any given moment.
 *
 * It exists because some actions must be real navigations rather than fetches —
 * an OAuth redirect has to be followed by the browser for the state cookie to
 * survive the round trip. Two screens had reimplemented button styling locally
 * to get that; both now share this.
 */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant = 'primary', size = 'md', leading, trailing, className, children, ...rest },
  ref,
) {
  return (
    <a ref={ref} className={cx(styles.button, styles[variant], styles[size], className)} {...rest}>
      {leading}
      {children}
      {trailing}
    </a>
  );
});
